import {
  IconPlus,
  IconExclamationTriangleOutline,
  IconFileWithItemsOutline,
} from '@iwoca/icons';
import { Button, Spinner } from '@iwoca/orion';
import { clsx } from 'clsx';

import styles from './UploadedFiles.module.css';
import { TFile } from '../../../hooks/useFileUploadReducer';
import { useLocale } from '../../../hooks/useLocale';

export type TFileStatus = 'loading' | 'error' | 'success' | 'rejected';

const UploadedFiles = ({
  files,
  removeFile,
}: {
  files: TFile[];
  removeFile: (id: string) => void;
}) => {
  const { isDE } = useLocale();

  const renderIcon = (status: TFileStatus) => {
    if (status === 'loading') {
      return <Spinner dataTestId="loading-icon" className={styles.spinner} />;
    }

    const uploadHasErrored = status === 'error' || status === 'rejected';

    if (uploadHasErrored) {
      return (
        <IconExclamationTriangleOutline
          data-testid={`${status}-icon`}
          width={20}
          height={20}
          className={clsx(styles.icon, {
            [styles.errorIcon]: status === 'error',
            [styles.rejectedIcon]: status === 'rejected',
          })}
        />
      );
    } else {
      return (
        <IconFileWithItemsOutline
          data-testid={'success-icon'}
          width={20}
          height={20}
        />
      );
    }
  };

  return (
    <>
      {files.map((file) => {
        const hasError = file.status === 'error';
        const isRejected = file.status === 'rejected';

        return (
          <div
            key={file.id}
            data-testid={`uploaded-file-${file.id}`}
            className={styles.uploadedFiles}
          >
            <div className={styles.iconContainer}>
              {renderIcon(file.status)}
            </div>
            <div className={styles.fileContent}>
              <p
                className={clsx(styles.name, {
                  [styles.errored]: hasError,
                  [styles.rejected]: isRejected,
                })}
              >
                {file.fileName}
              </p>
              {file.errorReason && !isRejected && (
                <p className={styles.errorMessage}>{file.errorReason}</p>
              )}
              {!file.errorReason && isRejected && !isDE && (
                <p
                  className={clsx(styles.errorMessage, styles.rejectedMessage)}
                >
                  {file.rejectReason}
                </p>
              )}
            </div>
            {hasError && (
              <Button
                data-testid="remove-button"
                size="small"
                variant="tertiary"
                onClick={() => removeFile(file.id)}
                className={styles.removeButton}
              >
                <IconPlus
                  height={20}
                  width={20}
                  className={styles.removeIcon}
                />
              </Button>
            )}
          </div>
        );
      })}
    </>
  );
};

export default UploadedFiles;
